import currency from "currency.js";
import {
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { OptionTypeResponse, SideResponse } from "../../../codegen-api";
import { MarketContext } from "../../../contexts/MarketContext";
import { MarketInstrumentContext } from "../../../contexts/MarketInstrumentContext";
import SegmentedControl from "../../shared/SegmentedControl";

import {
  COLORS,
  LAYER_COLORS,
  TEXT_COLORS,
} from "../../../constants/design/colors";
import { FONT_SIZE } from "../../../constants/design/fontSize";
import { COMPONENTS } from "../../../constants/design/spacing";
import { useOrder } from "../../../hooks/api/order/useOrder";
import useIndexWSS from "../../../hooks/wss/useIndexWSS";
import usePositionsWSS from "../../../hooks/wss/usePositionsWSS";
import CurrentSpotSection from "../CurrentSpotSection";
import DateSelection from "../DateSelection";
import {
  ITableContentRowWithCallPut,
  ITickerWithInstrumentId,
  MobileTableContent,
  MobileTableHeader,
} from "../OptionsTable";
import { OptionsChainContainer } from "../style";
import { useTickersStore } from "../../../hooks/wss/public/store/useTickersStore";
import {
  getAssetFromSymbol,
  getContractPriceStep,
  getExpiryFromUnix,
} from "../../../utils/instruments";
import { Divider } from "../../shared/Divider";
import {
  DataSelectionRow,
  MobileOptionsChainWrapper,
  MobileOptionsTypeSelectWrapper,
  StickyWrapper,
  TapToScroll,
} from "./style";
import MobileTradingBottomBar from "../../TradingBottomBar/MobileTradingBottomBar";
import Transition from "../../shared/Transition";
import MobileFooter from "../../MobileFooter";
import { ReactComponent as DownArrows } from "../../../assets/svg/chevrons-down.svg";
import OptionsTradeForm from "../../TradeForm/OptionsTradeForm";

interface IOptionsChainProps {
  activeInstrumentId: string | undefined;
  scrollableContainerRef: RefObject<HTMLDivElement>;
  showTradeForm: boolean;
  setShowTradeForm: (show: boolean) => void;
}

// Default placeholder ticker value used while loading
const defaultTickerValue: ITickerWithInstrumentId = {
  ask: {
    price: "",
    volume: "",
    iv: "",
    orderVolume: 0,
  },
  bid: {
    price: "",
    volume: "",
    iv: "",
    orderVolume: 0,
  },
  instrumentId: 0,
  instrumentName: "",
  position: 0,
  side: SideResponse.Buy,
  delta: "",
  open_interest: "0",
  markPrice: "",
  iv: "",
};

const placeholderRow: ITableContentRowWithCallPut = {
  strike: "",
  call: defaultTickerValue,
  put: defaultTickerValue,
  expiry: 0,
};

export function MobileOptionsChain({
  activeInstrumentId,
  scrollableContainerRef,
  showTradeForm,
  setShowTradeForm,
}: IOptionsChainProps) {
  const strikeWidth: number = 88;
  // rough estimation of space needed to center the table to strike
  const extraHeightForInitialScroll = 70;
  const { market } = useContext(MarketContext);
  const { index: indexData } = useIndexWSS(market.asset);
  const currentIndexPrice = indexData?.price;
  const {
    selectedValues,
    expiries,
    setExpiry,
    setOptionType,
    setStrike,
    selectedOptionInstrument,
    marketsData,
  } = useContext(MarketInstrumentContext);

  const { expiry } = selectedValues;
  const { positions, positionsMap } = usePositionsWSS();
  const { data: orders } = useOrder();

  const onTrade = useCallback(
    (orderType: OptionTypeResponse, exp: number, strike: string) => {
      setOptionType(orderType);
      setExpiry(exp);
      setStrike(strike);
      setShowTradeForm(true);
    },
    [setExpiry, setOptionType, setShowTradeForm, setStrike]
  );

  const marketsOfThisExpiry = useMemo(
    () => marketsData?.filter((v) => Number(v.expiry) === expiry) || [],
    [expiry, marketsData]
  );

  const putCalls = useMemo(
    () => ({
      calls: marketsOfThisExpiry.filter(
        (v) => v.option_type === OptionTypeResponse.Call
      ),
      puts: marketsOfThisExpiry.filter(
        (v) => v.option_type === OptionTypeResponse.Put
      ),
    }),
    [marketsOfThisExpiry]
  );

  // Websocket
  const addAssetDerivative = useTickersStore(
    (state) => state.addAssetDerivative
  );
  const removeAssetDerivative = useTickersStore(
    (state) => state.removeAssetDerivative
  );
  const instrumentTicker = useTickersStore(
    (state) => state.ticker[getExpiryFromUnix(expiry) || ""] || {}
  );

  const bottomBarRef = useRef<HTMLDivElement>(null);
  // As long as bottom bar tabs covers over half the screen, its considered showing
  const isBottomBarShowing =
    (bottomBarRef.current?.getBoundingClientRect().top || Infinity) <=
    document.body.clientHeight - COMPONENTS.mobileTradeBottomBarMinHeight;

  const currentSpotRef = useRef<HTMLDivElement>(null);
  const lowerThanSpotRowRefs = useRef<HTMLDivElement[]>([]);
  const higherThanSpotRowRefs = useRef<HTMLDivElement[]>([]);

  const { t } = useTranslation("app", {
    keyPrefix: "OptionsChain.MobileOptionsChain.MobileOptionsChain",
  });

  // Used for filtering in screensize < isMediumScreen
  const defaultMobileOrderType = useMemo(
    () => selectedValues.orderType || "call",
    [selectedValues.orderType]
  );

  const decoratedExpiries = useMemo(() => {
    const withPositions =
      positions
        .filter((pos) => !!pos.option && pos.asset === market.asset)
        .map((pos) => pos.option!.expiry) ?? [];
    const withOrders =
      orders
        ?.filter(
          (o) =>
            !!o.expiry && getAssetFromSymbol(o.instrument_name) === market.asset
        )
        .map((o) => o.expiry!) ?? [];
    return {
      withPositions,
      withOrders,
    };
  }, [market.asset, orders, positions]);

  // Returns rows, split into below strike price and above strike price
  const filteredRows = useMemo(() => {
    if (!currentIndexPrice) {
      return {
        lessThanCurrPriceRows: [
          placeholderRow,
          placeholderRow,
          placeholderRow,
          placeholderRow,
          placeholderRow,
        ],
        moreThanCurrPriceRows: [
          placeholderRow,
          placeholderRow,
          placeholderRow,
          placeholderRow,
          placeholderRow,
        ],
      };
    }

    const { puts, calls } = putCalls;
    const strikeToRow: Record<string, ITableContentRowWithCallPut> = {};

    calls.forEach((m) => {
      const ticker = instrumentTicker[m.instrument_name];
      if (m.strike && ticker) {
        const position = positionsMap[m.instrument_name];
        strikeToRow[m.strike] = {
          ...strikeToRow[m.strike],
          strike: m.strike,
          // Default value for put if doesnt already exist.
          put: strikeToRow[m.strike]?.put || defaultTickerValue,
          call: {
            delta: ticker.mark?.greeks?.delta || "0",
            open_interest: ticker.open_interest || "0",
            ask: {
              price: ticker.ask?.price || "0",
              volume: ticker.ask?.amount || "0",
              iv: ticker.ask?.greeks?.iv || "0",
              orderVolume: Number(
                orders?.find(
                  (o) =>
                    o.instrument_id === ticker.instrument_id &&
                    o.side === SideResponse.Sell
                )?.amount || 0
              ),
            },
            bid: {
              price: ticker.bid?.price || "0",
              volume: ticker.bid?.amount || "0",
              iv: ticker.bid?.greeks?.iv || "0",
              orderVolume: Number(
                orders?.find(
                  (o) =>
                    o.instrument_id === ticker.instrument_id &&
                    o.side === SideResponse.Buy
                )?.amount || 0
              ),
            },
            instrumentId: Number(m.instrument_id),
            instrumentName: m.instrument_name,
            position: Number(position?.amount || 0),
            side: position?.side || SideResponse.Buy,
            markPrice: ticker.mark?.price || "0",
            iv: ticker.mark?.greeks?.iv || "0",
          },
          expiry: expiry!,
        };
      }
    });
    puts.forEach((m) => {
      const ticker = instrumentTicker[m.instrument_name];
      const position = positionsMap[m.instrument_name];
      if (m.strike && ticker) {
        strikeToRow[m.strike] = {
          ...strikeToRow[m.strike],
          strike: m.strike,
          // Default value for call if doesnt already exist.
          call: strikeToRow[m.strike]?.call || defaultTickerValue,
          put: {
            delta: ticker.mark?.greeks?.delta || "0",
            open_interest: ticker.open_interest || "0",
            ask: {
              price: ticker.ask?.price || "0",
              volume: ticker.ask?.amount || "0",
              iv: ticker.ask?.greeks?.iv || "0",
              orderVolume: Number(
                orders?.find(
                  (o) =>
                    o.instrument_id === ticker.instrument_id &&
                    o.side === SideResponse.Sell
                )?.amount || 0
              ),
            },
            bid: {
              price: ticker.bid?.price || "0",
              volume: ticker.bid?.amount || "0",
              iv: ticker.bid?.greeks?.iv || "0",
              orderVolume: Number(
                orders?.find(
                  (o) =>
                    o.instrument_id === ticker.instrument_id &&
                    o.side === SideResponse.Buy
                )?.amount || 0
              ),
            },
            instrumentId: Number(m.instrument_id),
            instrumentName: m.instrument_name,
            position: Number(position?.amount || 0),
            side: position?.side || SideResponse.Buy,
            markPrice: ticker.mark?.price || "0",
            iv: ticker.mark?.greeks?.iv || "0",
          },
          expiry: expiry!,
        };
      }
    });

    const allRows = Object.values(strikeToRow);

    // Finally, split into 2 arrays, less than and more than current price
    let lessThanCurrPriceRows: ITableContentRowWithCallPut[] = [];
    let moreThanCurrPriceRows: ITableContentRowWithCallPut[] = [];

    if (allRows.length) {
      for (let i = 0; i < allRows.length; i += 1) {
        const row = allRows[i];
        if (currency(row.strike).value <= Number(currentIndexPrice)) {
          lessThanCurrPriceRows.push(row);
        } else {
          moreThanCurrPriceRows.push(row);
        }
      }
    } else {
      // Default values
      lessThanCurrPriceRows = [
        placeholderRow,
        placeholderRow,
        placeholderRow,
        placeholderRow,
        placeholderRow,
      ];
      moreThanCurrPriceRows = [
        placeholderRow,
        placeholderRow,
        placeholderRow,
        placeholderRow,
        placeholderRow,
      ];
    }

    return {
      lessThanCurrPriceRows,
      moreThanCurrPriceRows,
    };
  }, [
    putCalls,
    instrumentTicker,
    positionsMap,
    expiry,
    currentIndexPrice,
    orders,
  ]);

  useEffect(() => {
    if (expiry) {
      const channel = `${market.asset}:OPTION:${getExpiryFromUnix(
        expiry,
        true
      )}`;
      addAssetDerivative(channel);
      return () => {
        removeAssetDerivative(channel);
      };
    }
    return () => {};
  }, [addAssetDerivative, expiry, market.asset, removeAssetDerivative]);

  // ================================
  // ======== SCROLL JACKING ========
  // ================================
  // Whenever selected values changes, scrolls to spot price
  // also updates whenever no of rows changes

  useEffect(() => {
    if (scrollableContainerRef.current && currentSpotRef.current) {
      const halfScrollContainer =
        scrollableContainerRef.current.offsetHeight / 2;
      const halfCurrentSpotHeight = currentSpotRef.current.offsetHeight / 2;
      scrollableContainerRef.current.scrollTo({
        top:
          currentSpotRef.current.offsetTop -
          halfScrollContainer +
          halfCurrentSpotHeight +
          extraHeightForInitialScroll,
        behavior: "smooth",
      });
    }
  }, [
    filteredRows.lessThanCurrPriceRows.length,
    filteredRows.moreThanCurrPriceRows.length,
    scrollableContainerRef,
    selectedValues,
  ]);

  const onSelectExpiry = useCallback(
    (expiryDate: number) => {
      setExpiry(expiryDate);
    },
    [setExpiry]
  );

  const onSelectOptionTypeFilterMobile = useCallback(
    (value: string) => {
      setOptionType(value as OptionTypeResponse);
    },
    [setOptionType]
  );

  const onScrollToPortfolioTray = useCallback(() => {
    if (isBottomBarShowing) {
      scrollableContainerRef?.current?.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      return;
    }

    bottomBarRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  }, [isBottomBarShowing, scrollableContainerRef]);

  return (
    <>
      <MobileOptionsChainWrapper>
        <OptionsChainContainer>
          {showTradeForm && (
            <OptionsTradeForm
              selectedInstrument={selectedOptionInstrument}
              showTradeForm={showTradeForm}
              setShowTradeForm={setShowTradeForm}
              mobileMode
            />
          )}
          <StickyWrapper>
            <DataSelectionRow>
              <DateSelection
                expiries={expiries}
                selectedExpiry={expiry}
                expiriesWithPosition={decoratedExpiries.withPositions}
                expiriesWithOrder={decoratedExpiries.withOrders}
                onSelectExpiry={onSelectExpiry}
              />
            </DataSelectionRow>
            <MobileOptionsTypeSelectWrapper>
              <SegmentedControl
                segments={[
                  {
                    value: String(OptionTypeResponse.Call),
                    display: t("calls"),
                    textColor:
                      defaultMobileOrderType === OptionTypeResponse.Call
                        ? COLORS.blue.one
                        : TEXT_COLORS.three,
                  },
                  {
                    value: String(OptionTypeResponse.Put),
                    display: t("puts"),
                    textColor:
                      defaultMobileOrderType === OptionTypeResponse.Put
                        ? COLORS.blue.one
                        : TEXT_COLORS.three,
                  },
                ]}
                value={defaultMobileOrderType}
                onSelect={onSelectOptionTypeFilterMobile}
                config={{
                  theme: "outline",
                  color: COLORS.blue.one,
                  widthType: "fullWidth",
                  backgroundColor: LAYER_COLORS.two,
                  borderRadius: "10px",
                  button: {
                    fontSize: FONT_SIZE.one,
                  },
                }}
              />
              <Divider direction="vertical" size="100%" />
            </MobileOptionsTypeSelectWrapper>
            <MobileTableHeader
              strikeWidth={strikeWidth}
              expiry={expiry}
              mode={defaultMobileOrderType}
            />
          </StickyWrapper>
          <Transition>
            <MobileTableContent
              rows={filteredRows.lessThanCurrPriceRows}
              rowRefs={lowerThanSpotRowRefs}
              strikeWidth={strikeWidth}
              onTrade={onTrade}
              activeInstrumentId={activeInstrumentId}
              mode={defaultMobileOrderType}
              decimals={
                getContractPriceStep(selectedOptionInstrument).amount_precision
              }
            />
            <CurrentSpotSection
              ref={currentSpotRef}
              asset={market.asset}
              price={Number(currentIndexPrice)}
              loading={!currentIndexPrice}
            />
            <MobileTableContent
              rows={filteredRows.moreThanCurrPriceRows}
              rowRefs={higherThanSpotRowRefs}
              strikeWidth={strikeWidth}
              onTrade={onTrade}
              activeInstrumentId={activeInstrumentId}
              mode={defaultMobileOrderType}
              decimals={
                getContractPriceStep(selectedOptionInstrument).amount_precision
              }
            />
          </Transition>
        </OptionsChainContainer>
      </MobileOptionsChainWrapper>
      <MobileTradingBottomBar ref={bottomBarRef} />
      {
        // Only show footer if content is scrollable
        (scrollableContainerRef.current?.scrollHeight || 0) >
          document.body.clientHeight && (
          <MobileFooter>
            <TapToScroll
              key={String(isBottomBarShowing)}
              onClick={onScrollToPortfolioTray}
              reversed={isBottomBarShowing}
            >
              {!isBottomBarShowing
                ? t("tap_to_scroll_portfolio")
                : t("tap_to_scroll_top")}
              <DownArrows />
            </TapToScroll>
          </MobileFooter>
        )
      }
    </>
  );
}
